
import { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  Rectangle
} from 'recharts';
import { StockData } from '../data/sampleData';

interface CorrelationMatrixProps {
  stocksData: StockData[];
}

const CorrelationMatrix = ({ stocksData }: CorrelationMatrixProps) => {
  const [correlationData, setCorrelationData] = useState<any[]>([]);
  
  useEffect(() => {
    if (stocksData.length > 1) {
      // Generate correlation data for matrix
      const matrix: any[] = [];
      
      for (let i = 0; i < stocksData.length; i++) {
        for (let j = 0; j < stocksData.length; j++) {
          // Calculate correlation - in real world would use proper pearson correlation
          // This is simplified for demonstration
          const correlation = i === j ? 1 : 0.99 - (Math.random() * 0.4 + 0.05);
          
          matrix.push({
            x: stocksData[i].symbol,
            y: stocksData[j].symbol,
            z: correlation,
            color: getCorrelationColor(correlation)
          });
        }
      }
      
      setCorrelationData(matrix);
    }
  }, [stocksData]);
  
  // Calculate color based on correlation strength
  const getCorrelationColor = (correlation: number): string => {
    if (correlation >= 0.9) return '#dc2626'; // Strong red
    if (correlation >= 0.8) return '#ef4444'; // Red
    if (correlation >= 0.7) return '#f97316'; // Orange
    if (correlation >= 0.6) return '#eab308'; // Yellow
    if (correlation >= 0.5) return '#84cc16'; // Light green
    if (correlation >= 0.4) return '#22c55e'; // Green
    if (correlation >= 0.3) return '#14b8a6'; // Teal
    return '#3b82f6'; // Blue (weak correlation)
  };
  
  if (!correlationData.length) {
    return <div className="text-center py-8">Select multiple datasets to view correlation matrix</div>;
  }
  
  const CustomizedRectangle = (props: any) => {
    const { x, y, width, height, index } = props;
    
    // Safety check: ensure index is valid and the item exists
    if (index === undefined || index < 0 || index >= correlationData.length) {
      return null;
    }
    
    const item = correlationData[index];
    
    // Another safety check: ensure item and item.color exist
    if (!item || !item.color) {
      return null;
    }
    
    // Make sure all values are valid numbers
    const rectX = typeof x === 'number' ? x : 0;
    const rectY = typeof y === 'number' ? y : 0;
    const rectWidth = typeof width === 'number' && !isNaN(width) && width > 0 ? width : 10;
    const rectHeight = typeof height === 'number' && !isNaN(height) && height > 0 ? height : 10;
    
    return (
      <g>
        <Rectangle
          x={rectX}
          y={rectY}
          width={rectWidth}
          height={rectHeight}
          fill={item.color}
        />
        <text
          x={rectX + rectWidth / 2}
          y={rectY + rectHeight / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#ffffff"
          fontSize={12}
        >
          {item.z.toFixed(2)}
        </text>
      </g>
    );
  };

  const renderTooltip = (props: any) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip bg-card border p-2 rounded-md shadow-sm">
          <p className="label text-sm font-medium">{`${data.x} × ${data.y}`}</p>
          <p className="desc text-sm">Correlation: {data.z.toFixed(3)}</p>
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="w-full bg-card border rounded-xl overflow-hidden p-6 h-[400px]">
      <h3 className="text-xl font-semibold mb-4">Correlation Matrix</h3>
      <ResponsiveContainer width="100%" height="90%">
        <ScatterChart
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="x" 
            type="category" 
            name="Stock" 
            allowDuplicatedCategory={false} 
          />
          <YAxis 
            dataKey="y" 
            type="category" 
            name="Stock" 
            allowDuplicatedCategory={false} 
          />
          <ZAxis 
            dataKey="z" 
            range={[0, 0]} 
            name="Correlation" 
          />
          <Tooltip content={renderTooltip} />
          <Scatter
            data={correlationData}
            shape={<CustomizedRectangle />}
          />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CorrelationMatrix;
